import React, { RefObject, useState, useEffect, useMemo, JSX } from 'react';
import { CardHeader, Card, CardBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ApiFilter,
  ApiFilterCategory,
  ApiGroupedMultipleSelectionFilter,
  ApiMultipleSelectionFilter,
  ApiNumberRangeFilter,
  ApiSingleSelectionFilter
} from '@ibe/api';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import Keys from '../../../_Translations/generated/service-list.en.t.json.keys';
import fallback from '../../../_Translations/generated/service-list.en.t.json';
import calculateAppliedFiltersCount, { FilterProperties } from './externals';
import FilterDiscard from './FilterDiscard';
import FilterComponent from './FilterComponent';
import useTranslation from '../../Hooks/useTranslation';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';
import useConfig from '../../Hooks/useConfig';
import { throttle } from 'lodash-es';
import useWindow from '../../Hooks/useWindow';
import { FilterOverrides } from '../../Components/DynamicList/FilterOptions/FilterOptions';

const FilterDesktopView = ({
  filterProps,
  filteredItemCount,
  fullItemCount,
  filtersFromApi,
  useIcons,
  searchWidgetContainerRef,
  searchWidgetChangedSize,
  expectedOffset,
  filterOverrides,
  filterLabelTranslationMappings,
  showResultsCountInParenthesis,
  showEmptyOptionsWhenFiltering,
  showResultsCount,
  showResetButtonForFilter
}: {
  filterProps?: FilterProperties;
  filteredItemCount?: number;
  fullItemCount?: number;
  filtersFromApi: ApiFilter[];
  useIcons?: boolean;
  searchWidgetContainerRef?: RefObject<HTMLDivElement | null>;
  searchWidgetChangedSize?: boolean;
  expectedOffset?: number;
  filterOverrides?: FilterOverrides;
  filterLabelTranslationMappings?: Record<string, (label: string) => string>;
  showResultsCountInParenthesis?: boolean;
  showResultsCount?: Partial<Record<ApiFilterCategory, boolean>>;
  showEmptyOptionsWhenFiltering?: boolean;
  showResetButtonForFilter?: boolean;
}): JSX.Element => {
  const window = useWindow();
  const { t } = useTranslation('service-list', fallback, Keys);
  const config = useConfig();
  const { stickyElementsOffset } = config;
  const [offset, setOffset] = useState<number>();
  const isTablet = useMediaQuery({
    type: 'min',
    query: stickyElementsOffset?.tablet?.breakpoint?.toString() || MEDIAQUERY_DEFAULTS.md
  });
  const isDesktop = useMediaQuery({
    type: 'min',
    query: stickyElementsOffset?.desktop?.breakpoint?.toString() || MEDIAQUERY_DEFAULTS.lg
  });

  const calculateOffset = () => {
    let newOffset = searchWidgetContainerRef?.current?.clientHeight || 0;

    if (expectedOffset !== undefined) {
      newOffset = expectedOffset;
    }

    if (stickyElementsOffset) {
      if (isTablet && !isDesktop) {
        newOffset = newOffset + stickyElementsOffset.tablet.offset;
      } else if (isDesktop) {
        newOffset = newOffset + stickyElementsOffset.desktop.offset;
      } else {
        newOffset = newOffset + stickyElementsOffset.mobile.offset;
      }
    }
    setOffset(newOffset);
  };

  useEffect(() => {
    setTimeout(() => {
      calculateOffset();
    }, 100);
  }, [
    searchWidgetContainerRef,
    searchWidgetContainerRef?.current?.clientHeight,
    stickyElementsOffset,
    isDesktop,
    isTablet,
    searchWidgetChangedSize
  ]);

  useEffect(() => {
    const debouncedOffsetUpdate = throttle(calculateOffset, 1000);
    window?.addEventListener('scroll', debouncedOffsetUpdate);

    return () => window?.removeEventListener('scroll', debouncedOffsetUpdate);
  }, []);

  const showEmptyOptionsWhenFilteringIsActive = useMemo(() => {
    if (showEmptyOptionsWhenFiltering) {
      let someFilterSelectionOptionIsActive = false;
      filtersFromApi.forEach((f: ApiFilter) => {
        switch (f.type) {
          case 'SingleSelectionFilter': {
            const singleSelectionFilter = f as ApiSingleSelectionFilter;
            if (singleSelectionFilter.selectedOption) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          }
          case 'GroupedMultipleSelectionFilter': {
            const groupMultiFilter = f as ApiGroupedMultipleSelectionFilter;
            if (groupMultiFilter.selectedOptions) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          }
          case 'MultipleSelectionFilter': {
            const multiSelectFilter = f as ApiMultipleSelectionFilter;
            if (multiSelectFilter.selectedOptions) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          }
          case 'NumberRangeFilter': {
            const numberRangeFilter = f as ApiNumberRangeFilter;
            if (numberRangeFilter.maxSelected || numberRangeFilter.minSelected) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          }
          default:
        }
      });
      return someFilterSelectionOptionIsActive;
    }
    return false;
  }, [filtersFromApi]);

  return (
    <Card
      className="iso__serviceList__filterContainer__filters"
      style={{
        top: searchWidgetContainerRef && `${offset}px`
      }}
    >
      <CardHeader className="d-flex justify-content-between">
        <div className="card-header__filters-count">
          <FontAwesomeIcon icon={faFilter} />{' '}
          <strong>
            {t(Keys.filter)} {calculateAppliedFiltersCount(filterProps)}
          </strong>
        </div>
        <div className="card-header__results">
          <strong>
            {filteredItemCount &&
              fullItemCount &&
              `${+filteredItemCount} / ${fullItemCount} ${t(Keys.filterResults)}`}
          </strong>
        </div>
      </CardHeader>
      <CardBody>
        <FilterDiscard filterProps={filterProps} />
        {filtersFromApi.map((f: ApiFilter, i: number) => (
          <FilterComponent
            key={`${f.type}-${f.itemType}-${f.category}`}
            f={f}
            filterOrdinalNumber={i}
            isCollapsable
            filterProps={filterProps}
            useIcons={useIcons}
            filterOverrides={filterOverrides}
            filterLabelTranslationMappings={filterLabelTranslationMappings}
            showResultsCountInParenthesis={showResultsCountInParenthesis}
            showResultsCount={showResultsCount}
            showEmptyOptionsWhenFiltering={showEmptyOptionsWhenFilteringIsActive}
            showResetButtonForFilter={showResetButtonForFilter}
          />
        ))}
      </CardBody>
    </Card>
  );
};

export default FilterDesktopView;
